import { trpcVanillaForViewer } from "@client/trpc";
import ClientInfoStore from "@engine/round/infoStore/ClientInfoStore";
import { isTransportError } from "@client/offline/offlineCache";
import { isIPadCapacitor } from "@client/lib/isIPad";
import { type UserProfiles } from "@lib/types";
import { isFunderReviewPath } from "@lib/funderWorkspaceRoutes";
import {
  prefetchWorkspaceMeetings,
  workspaceMeetingTargetFromSearch,
} from "./funder-workspace/prefetchWorkspaceMeetings";
import {
  prefetchWorkspaceProposalClips,
  workspaceExcerptTargetFromSearch,
} from "./funder-workspace/prefetchWorkspaceProposalClips";
import { prefetchWorkspaceRecommenderClips } from "./funder-workspace/prefetchWorkspaceRecommenderClips";
import { prefetchWorkspaceCorpus } from "./funder-workspace/prefetchWorkspaceCorpus";
import { buildAuthHeadersForViewer, getClientBaseUrl } from "@client/trpc";
import {
  recordObservedAggregateSize,
  isCacheFirstEnabled,
} from "@lib/cacheFirstRounds";
import { type RoundAggregate } from "@engine/round/aggregate";
import {
  AGGREGATE_LOG_REVISION_HEADER,
  type DownloadedRound,
} from "@engine/storageAdapters/downloadedRound";
import { type QueryClient } from "@tanstack/react-query";
import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { RoundLoadError, isUnansweredStatus } from "./roundLoadError";

export async function downloadRound(
  roundId: string,
  impersonatedUserId: string | null,
  signal?: AbortSignal,
): Promise<DownloadedRound> {
  // Hit the Express byte-cache route rather than tRPC's getAggregate: tRPC
  // re-serializes its return value, so it can't serve the precomputed gzipped
  // aggregate. `buildAuthHeadersForViewer` carries impersonation + native auth.
  // browser negotiates and transparently inflates gzip via Accept-Encoding.
  //
  // `credentials: "same-origin"` (matching the tRPC client's default) sends the
  // session cookie on web, where this is a same-origin request. On bundled
  // native builds the request is cross-origin to the API host, whose CORS
  // policy deliberately does NOT allow credentials — native auth rides the
  // bearer token in the auth headers instead. Using "include" here would
  // make the browser reject those native responses at the CORS check.
  const url = `${getClientBaseUrl()}/api/round-aggregate?roundId=${encodeURIComponent(
    roundId,
  )}`;
  const response = await fetch(url, {
    method: "GET",
    signal,
    credentials: "same-origin",
    headers: buildAuthHeadersForViewer(impersonatedUserId),
  });
  if (response.status === 404) {
    throw new RoundLoadError("not-found", "Round not found");
  }
  if (response.status === 401 || response.status === 403) {
    throw new RoundLoadError(
      "forbidden",
      "You do not have access to this round",
    );
  }
  if (!response.ok) {
    // A gateway-style status (502/503/504, or a timeout/rate limit) is a deploy
    // gap, a restarting dev backend, or an unavailable database — not a broken
    // round. Classified as "connection" so it reads as transient to the person
    // AND auto-retries below, instead of dead-ending on "Failed to load round
    // data" for a server that is about to be fine. A plain 500 is NOT in that
    // set: this endpoint raises one itself when the aggregate genuinely fails
    // to build, and that keeps its own message and its single attempt.
    if (isUnansweredStatus(response.status)) {
      throw new RoundLoadError(
        "connection",
        `Couldn't reach the server (status ${response.status})`,
      );
    }
    throw new RoundLoadError(
      "generic",
      `Failed to load round data (status ${response.status})`,
    );
  }
  // Read as text so the payload size can be recorded for the learned
  // cache-first decision (see cacheFirstRounds.ts).
  const text = await response.text();
  recordObservedAggregateSize(roundId, impersonatedUserId, text.length);
  return {
    aggregate: JSON.parse(text) as RoundAggregate,
    // Exposed to the native app's cross-origin fetch by the server's CORS
    // middleware (Access-Control-Expose-Headers), so every client that talks
    // to a current server receives it. Absent only against a server that
    // does not send it yet; the cache row then records no revision.
    logRevision: response.headers.get(AGGREGATE_LOG_REVISION_HEADER),
  };
}

export type PreparedRound = DownloadedRound & {
  loadStartedAt: number;
  infoStore: ClientInfoStore;
  userProfilesPromise: Promise<UserProfiles> | null;
  viewerIsSpeculatorPromise: Promise<boolean>;
};

// Shared by Home preparation and direct round loads: start independent reads
// together, while preserving the Review route's deferred proposal-text policy.
export function createRoundInfoStore(
  roundId: string,
  viewAsUserId: string | null,
  pagePath: string,
) {
  const api = trpcVanillaForViewer(viewAsUserId);
  const infoStore = new ClientInfoStore(
    (fieldResponsesIds) =>
      api.getFieldResponses.query({ roundId, fieldResponsesIds }),
    undefined,
    (error) => !getIsOnline() || isTransportError(error),
  );
  if (!isFunderReviewPath(roundId, `/round/${roundId}${pagePath}`)) {
    void infoStore.prefetchAllCurrentIfCold(() =>
      api.getFieldResponses.query({ roundId }),
    );
  }
  return infoStore;
}

export function prefetchRoundWorkspace(
  queryClient: QueryClient,
  roundId: string,
  pagePath: string,
  search: string,
) {
  const pathname = `/round/${roundId}${pagePath}`;
  prefetchWorkspaceMeetings(
    queryClient,
    roundId,
    pathname,
    workspaceMeetingTargetFromSearch(search),
  );
  prefetchWorkspaceProposalClips(
    queryClient,
    roundId,
    pathname,
    workspaceExcerptTargetFromSearch(search),
  );
  prefetchWorkspaceRecommenderClips(
    queryClient,
    roundId,
    pathname,
    workspaceExcerptTargetFromSearch(search),
  );
  prefetchWorkspaceCorpus(queryClient, roundId, pathname);
}

export type RoundEntryViewer = { userId: string; viewAsUserId: string | null };
const PREPARED_ROUND_MAX_AGE = 30_000;

export function roundEntryQueryKey(roundId: string, viewer: RoundEntryViewer) {
  return ["round-entry", roundId, viewer.userId, viewer.viewAsUserId] as const;
}

// Prepares independent reads; storage, queued edits, and the live client still
// belong to Round. Cache-first/offline entry keeps its existing cheaper path.
export async function prepareRoundEntry(
  queryClient: QueryClient,
  roundId: string,
  viewer: RoundEntryViewer,
  pagePath = "/",
  search = "",
) {
  if (!getIsOnline() || isCacheFirstEnabled(roundId, viewer.viewAsUserId))
    return;
  prefetchRoundWorkspace(queryClient, roundId, pagePath, search);
  await queryClient.fetchQuery({
    queryKey: roundEntryQueryKey(roundId, viewer),
    queryFn: async ({ signal }): Promise<PreparedRound> => {
      const loadStartedAt = performance.now();
      const aggregatePromise = downloadRound(
        roundId,
        viewer.viewAsUserId,
        signal,
      );
      const api = trpcVanillaForViewer(viewer.viewAsUserId);
      const infoStore = createRoundInfoStore(
        roundId,
        viewer.viewAsUserId,
        pagePath,
      );
      // iPad profiles still start inside their viewer-keyed offline wrapper.
      const userProfilesPromise = isIPadCapacitor()
        ? null
        : api.getUserProfiles.query({ roundId });
      userProfilesPromise?.catch(() => {});
      const viewerIsSpeculatorPromise = api.getViewerIsSpeculator
        .query()
        .catch(() => false);
      const download = await aggregatePromise;
      return {
        ...download,
        loadStartedAt,
        infoStore,
        userProfilesPromise,
        viewerIsSpeculatorPromise,
      };
    },
    staleTime: 0,
    gcTime: PREPARED_ROUND_MAX_AGE,
    retry: false,
  });
}

export function readPreparedRound(
  queryClient: QueryClient,
  roundId: string,
  viewer: RoundEntryViewer,
) {
  const state = queryClient.getQueryState<PreparedRound>(
    roundEntryQueryKey(roundId, viewer),
  );
  return state?.status === "success" &&
    Date.now() - state.dataUpdatedAt < PREPARED_ROUND_MAX_AGE
    ? state.data
    : undefined;
}
